
'use strict';

import { id } from '../utils/id';

const KEYCODE = {
	UP: 38,
	DOWN: 40,
	RETURN: 13,
	ESC: 27
};

export function Dropdown(params) {
	/* TODO: adapt with input text instead of cta */
	let dropdownElement = null;
	let ctaElement = null;
	let listboxElement = null;
	let inputElement = null;

	let dropdownId = '';

	let selectOptions = new Array();
	let selectedOptionId = null;

	let ctaPlaceholderLabel = '';

	const dropdownInvalidModifier = 'dropdown--invalid';
	const hiddenListboxModifier = 'dropdown__options-list--hidden';
	const focusedListItemModifier = 'dropdown__option--focused';
	const activeCtaModifier = 'dropdown__cta--active';
	const withSpinnerCtaModifier = 'dropdown__cta--with-spinner';
	const emptyOptionsListCtaModifier = 'dropdown__cta--with-empty-options-list';

	let isDropdownActive = false;

	if (params.selectElement) {
		const selectElement = params.selectElement.cloneNode(true);
		dropdownId = selectElement.id || id();

		dropdownElement = function () {
			const element = document.createElement('div');
			element.setAttribute('id', dropdownId);

			if (selectElement.dataset.dropdownClass) {
				element.setAttribute('class', selectElement.dataset.dropdownClass);
			}

			element.classList.add('dropdown');

			return element;
		}();

		ctaElement = function () {
			const element = document.createElement('span');
			element.setAttribute('id', dropdownId + '__cta');
			element.setAttribute('role', 'button');
			element.setAttribute('tabindex', '0');
			element.setAttribute('aria-haspopup', 'listbox');
			element.classList.add('dropdown__cta');
			if(params.labelId) element.setAttribute('aria-labelledby', params.labelId);

			const selectedOptionElement = selectElement.querySelector('option[selected]');
			if (selectedOptionElement && selectedOptionElement.innerText !== '') {
				element.innerText = selectedOptionElement.innerText;
			}

			const selectPlaceholderOptionElement = selectElement.querySelector('option[disabled]:first-child');
			if (selectPlaceholderOptionElement && selectPlaceholderOptionElement.innerText !== '') {
				ctaPlaceholderLabel = selectPlaceholderOptionElement.innerText;

				if (!element.innerText.length) {
					element.innerText = ctaPlaceholderLabel;
				}
			}

			return element;
		}();

		listboxElement = function () {
			const element = document.createElement('ul');
			element.setAttribute('id', dropdownId + '__options');
			element.setAttribute('role', 'listbox');
			element.setAttribute('tabindex', '-1');
			element.classList.add('dropdown__options-list');
			element.classList.add(hiddenListboxModifier);
			element.setAttribute('aria-labelledby', dropdownId + '__cta' + (params.labelId ? ' ' + params.labelId : ''));

			const selectOptionElements = selectElement.querySelectorAll('option');

			for (let i = 0; i < selectOptionElements.length; i++) {
				const selectOptionElement = selectOptionElements[i];
				if (selectOptionElement.value) {
					const itemElement = document.createElement('li');
					itemElement.setAttribute('id', dropdownId + '__option-' + i);
					itemElement.setAttribute('role', 'option');
					itemElement.classList.add('dropdown__option');
					itemElement.innerText = selectOptionElement.innerText;

					selectOptions[dropdownId + '__option-' + i] = selectOptionElement.value;

					if (selectOptionElement.selected) {
						selectedOptionId = dropdownId + '__option-' + i;
						itemElement.classList.add(focusedListItemModifier);
					}

					element.append(itemElement);
				}
			}

			return element;
		}();

		inputElement = function () {
			if (selectElement.hasAttribute('name')) {
				const element = document.createElement('input');
				element.setAttribute('name', selectElement.getAttribute('name'));
				element.setAttribute('type', 'text');
				element.setAttribute('style', 'display:none');
				if (selectElement.required) element.setAttribute('required', true);

				if (selectElement.value) {
					element.setAttribute('value', selectElement.value);
				}

				if (!element.validity.valid) {
					dropdownElement.classList.add(dropdownInvalidModifier);
				}

				return element;
			}

			return null;
		}();

		params.selectElement.parentNode.insertBefore(dropdownElement, params.selectElement.nextElementSibling);
		params.selectElement.parentNode.removeChild(params.selectElement);
		dropdownElement.append(ctaElement);
		dropdownElement.append(listboxElement);
		if (inputElement) dropdownElement.append(inputElement);

		if (!Object.keys(selectOptions).length) {
			ctaElement.classList.add(emptyOptionsListCtaModifier);
		}

	} else if (params.dropdownElement) {
		dropdownElement = params.dropdownElement;
		dropdownId = dropdownElement.id;
		ctaElement = dropdownElement.querySelector('.dropdown__cta');
		listboxElement = dropdownElement.querySelector('.dropdown__options-list');
		inputElement = dropdownElement.querySelector('input');
		selectOptions = dropdownElement.dataset && dropdownElement.dataset.values ? JSON.parse(dropdownElement.dataset.values) : {};
		ctaPlaceholderLabel = !!ctaElement ? ctaElement.innerHTML || '' : '';
	}

	function resetSelection() {
		selectedOptionId = null;

		if (inputElement) {
			inputElement.value = '';

			if (!inputElement.validity.valid && !dropdownElement.classList.contains(dropdownInvalidModifier)) {
				dropdownElement.classList.add(dropdownInvalidModifier);
			}
		}

		listboxElement.removeAttribute('aria-activedescendant');

		ctaElement.innerHTML = ctaPlaceholderLabel;
	}

	function showListbox() {
		ctaElement.setAttribute('aria-expanded', 'true');

		if (!ctaElement.classList.contains(activeCtaModifier)) {
			ctaElement.classList.add(activeCtaModifier);
		}

		if (listboxElement.classList.contains(hiddenListboxModifier)) {
			listboxElement.classList.remove(hiddenListboxModifier);
		}

		listboxElement.focus();

		isDropdownActive = true;
	}

	function hideListbox() {
		ctaElement.removeAttribute('aria-expanded');

		if (ctaElement.classList.contains(activeCtaModifier)) {
			ctaElement.classList.remove(activeCtaModifier);
		}

		if (!listboxElement.classList.contains(hiddenListboxModifier)) {
			listboxElement.classList.add(hiddenListboxModifier);
		}

		ctaElement.focus();

		isDropdownActive = false;
	}

	function unSelectOption(element) {
		if (element && element.classList.contains(focusedListItemModifier)) {
			element.classList.remove(focusedListItemModifier);
			if (element.hasAttribute('aria-selected')) {
				element.setAttribute('aria-selected', 'true');
			}
		}

		resetSelection();
	}

	function selectOption(element) {
		if (element) {
			if (!element.classList.contains(focusedListItemModifier)) {
				element.classList.add(focusedListItemModifier);
			}

			if (element.id !== selectedOptionId) {
				if(selectedOptionId) {
					unSelectOption(document.getElementById(selectedOptionId));
				}

				selectedOptionId = element.id;
				element.setAttribute('aria-selected', 'true');
				listboxElement.setAttribute('aria-activedescendant', selectedOptionId);

				if (inputElement) {
					inputElement.value = selectOptions[selectedOptionId];

					if (!inputElement.validity.valid && !dropdownElement.classList.contains(dropdownInvalidModifier)) {
						dropdownElement.classList.add(dropdownInvalidModifier);
					} else if (inputElement.validity.valid && dropdownElement.classList.contains(dropdownInvalidModifier)) {
						dropdownElement.classList.remove(dropdownInvalidModifier);
					}
				}

				ctaElement.innerText = element.innerText;
			}

			if (listboxElement.scrollHeight > listboxElement.clientHeight) {
				const listboxScrollBottom = listboxElement.clientHeight + listboxElement.scrollTop;
				const elementBottom = element.offsetTop + element.offsetHeight;
				if (elementBottom > listboxScrollBottom) {
					listboxElement.scrollTop = elementBottom - listboxElement.clientHeight;
				} else if (element.offsetTop < listboxElement.scrollTop) {
					listboxElement.scrollTop = element.offsetTop;
				}
			}

			if (params.onChange && typeof params.onChange === 'function') {
				params.onChange(selectOptions[element.id]);
			}
		} else if(selectedOptionId) {
			unSelectOption(document.getElementById(selectedOptionId));
		}
	}

	function selectPreviousOption() {
		const selectedOptionElement = selectedOptionId !== null ? document.getElementById(selectedOptionId) : null;
		const previousOptionElement = selectedOptionElement ? selectedOptionElement.previousElementSibling : null;

		if (previousOptionElement && previousOptionElement.getAttribute('role') === 'option') {
			selectOption(previousOptionElement);
		} else {
			selectOption(listboxElement.querySelector('[role=option]'));
		}
	}

	function selectNextOption() {
		const selectedOptionElement = selectedOptionId !== null ? document.getElementById(selectedOptionId) : null;

		if (!selectedOptionElement) {
			selectOption(listboxElement.querySelector('[role=option]'));
		} else {
			const nextOptionElement = selectedOptionElement.nextElementSibling;
			if (nextOptionElement && nextOptionElement.getAttribute('role') === 'option') {
				selectOption(nextOptionElement);
			} else {
				selectOption(listboxElement.querySelector('[role=option]:last-child'));
			}
		}
	}

	function keyDownInteractions(event) {
		const key = event.which || event.keyCode;

		if (key) {
			switch (key) {
				case KEYCODE.UP:
				case KEYCODE.DOWN:
					event.preventDefault();
					showListbox();
					if (key === KEYCODE.UP) selectPreviousOption();
					if (key === KEYCODE.DOWN) selectNextOption();
					break;
				case KEYCODE.RETURN:
					event.preventDefault();
					if (isDropdownActive) {
						hideListbox();
						
						if (params.onEnter && typeof params.onEnter === 'function') {
							params.onEnter(selectOptions[selectedOptionId]);
						}
					} else {
						showListbox();
					}
					break;
				case KEYCODE.ESC:
					event.preventDefault();
					hideListbox();
					break;
			}
		}
	}

	ctaElement.addEventListener('click', function (event) {
		event.preventDefault();
		if (isDropdownActive) {
			hideListbox();			
		} else {
			showListbox();
		}
	});

	listboxElement.addEventListener('blur', function (event) {
		event.preventDefault();
		if (event.relatedTarget === ctaElement) return;
		hideListbox();
	});

	listboxElement.addEventListener('click', function (event) {
		event.preventDefault();
		if (event.target.getAttribute('role') === 'option') {
			selectOption(event.target);
		}

		if (isDropdownActive) {
			hideListbox();
		}

		if (params.onEnter && typeof params.onEnter === 'function') {
			params.onEnter(selectOptions[selectedOptionId]);
		}
	});

	ctaElement.addEventListener('keydown', keyDownInteractions);
	listboxElement.addEventListener('keydown', keyDownInteractions);

	function updateOptions(options) {
		if (!options.length) {
			if (!ctaElement.classList.contains(emptyOptionsListCtaModifier)) {
				ctaElement.classList.add(emptyOptionsListCtaModifier);
			}
		} else {
			if (ctaElement.classList.contains(emptyOptionsListCtaModifier)) {
				ctaElement.classList.remove(emptyOptionsListCtaModifier);
			}
		}

		resetSelection();

		if (listboxElement.hasChildNodes()) {
			while (listboxElement.firstChild) {
				listboxElement.removeChild(listboxElement.firstChild);
			}
		}

		selectOptions = [];

		for (let i = 0; i < options.length; i++) {
			const itemElement = document.createElement('li');
			itemElement.setAttribute('id', dropdownId + '__option-' + i);
			itemElement.setAttribute('role', 'option');
			itemElement.classList.add('dropdown__option');
			itemElement.innerText = options[i].label;

			selectOptions[dropdownId + '__option-' + i] = options[i].value;

			if (options[i].selected) {
				selectOption(itemElement);
			}

			listboxElement.append(itemElement);
		}
	}

	function showSpinner() {
		if (!ctaElement.classList.contains(withSpinnerCtaModifier)) {
			ctaElement.classList.add(withSpinnerCtaModifier);
		}
	}

	function hideSpinner() {
		if (ctaElement.classList.contains(withSpinnerCtaModifier)) {
			ctaElement.classList.remove(withSpinnerCtaModifier);
		}
	}

	function getSelectedOptionValue() {
		return selectOptions[selectedOptionId];
	}

	return {
		id: dropdownId,
		updateOptions: updateOptions,
		selectOption: selectOption,
		showSpinner: showSpinner,
		hideSpinner: hideSpinner,
		getSelectedOptionValue: getSelectedOptionValue
	}
}